import React from 'react';
import Icon from 'components/AppIcon';

export default function NavigationProgressTracker({
  steps = [],
  currentStep = 0,
  onStepClick,
  orientation = 'horizontal', // 'horizontal' | 'vertical'
}) {
  const getStepStatus = (index) => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  const canNavigateTo = (index) => index <= currentStep;

  const handleStepClick = (index) => {
    if (canNavigateTo(index) && onStepClick) {
      onStepClick(index);
    }
  };

  if (orientation === 'vertical') {
    return (
      <nav aria-label="Progress" className="flex flex-col gap-0">
        {steps?.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps?.length - 1;

          return (
            <div key={index} className="flex gap-4">
              {/* Indicator + Connector column */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => handleStepClick(index)}
                  disabled={!canNavigateTo(index)}
                  className={`progress-step-indicator ${status} transition-all duration-250 ${
                    canNavigateTo(index) ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'
                  }`}
                  aria-label={`Step ${index + 1}: ${step?.label} - ${status}`}
                  aria-current={status === 'active' ? 'step' : undefined}
                >
                  {status === 'completed' ? (
                    <Icon name="Check" size={14} strokeWidth={2.5} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                {!isLast && (
                  <div
                    className={`progress-connector w-0.5 flex-1 min-h-8 ${
                      status === 'completed' ? 'completed' : ''
                    }`}
                    style={{ width: '2px' }}
                  />
                )}
              </div>
              {/* Step content */}
              <div className={`pb-6 ${isLast ? 'pb-0' : ''} pt-1`}>
                <button
                  onClick={() => handleStepClick(index)}
                  disabled={!canNavigateTo(index)}
                  className={`text-left transition-all duration-250 ${
                    canNavigateTo(index) ? 'cursor-pointer' : 'cursor-not-allowed'
                  }`}
                >
                  <p
                    className={`font-caption font-medium text-sm ${
                      status === 'active' ?'text-primary'
                        : status === 'completed' ?'text-success' :'text-muted-foreground'
                    }`}
                  >
                    {step?.label}
                  </p>
                  {step?.description && (
                    <p className="font-caption text-muted-foreground mt-0.5" style={{ fontSize: '0.75rem' }}>
                      {step?.description}
                    </p>
                  )}
                  {step?.validationError && status === 'active' && (
                    <p className="font-caption text-error mt-1" style={{ fontSize: '0.75rem' }}>
                      <Icon name="AlertCircle" size={12} className="inline mr-1" />
                      {step?.validationError}
                    </p>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </nav>
    );
  }

  // Horizontal layout
  return (
    <nav aria-label="Progress" className="w-full">
      {/* Desktop horizontal */}
      <div className="hidden sm:flex items-center w-full">
        {steps?.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === steps?.length - 1;

          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => handleStepClick(index)}
                  disabled={!canNavigateTo(index)}
                  className={`progress-step-indicator ${status} ${
                    canNavigateTo(index) ? 'cursor-pointer hover:opacity-80' : 'cursor-not-allowed'
                  }`}
                  aria-label={`Step ${index + 1}: ${step?.label} - ${status}`}
                  aria-current={status === 'active' ? 'step' : undefined}
                >
                  {status === 'completed' ? (
                    <Icon name="Check" size={14} strokeWidth={2.5} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                <div className="text-center max-w-24">
                  <p
                    className={`font-caption font-medium whitespace-nowrap ${
                      status === 'active' ?'text-primary'
                        : status === 'completed' ?'text-success' :'text-muted-foreground'
                    }`}
                    style={{ fontSize: '0.75rem' }}
                  >
                    {step?.label}
                  </p>
                  {step?.validationError && status === 'active' && (
                    <Icon name="AlertCircle" size={12} color="var(--color-error)" className="mt-0.5" />
                  )}
                </div>
              </div>
              {!isLast && (
                <div
                  className={`progress-connector flex-1 mx-2 ${
                    status === 'completed' ? 'completed' : ''
                  }`}
                  style={{ marginBottom: '20px' }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Mobile vertical compact */}
      <div className="flex sm:hidden items-center gap-3">
        <div className="flex items-center gap-1.5">
          {steps?.map((_, index) => {
            const status = getStepStatus(index);
            return (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-250 ${
                  status === 'completed'
                    ? 'bg-success'
                    : status === 'active' ?'bg-primary' :'bg-muted'
                }`}
                style={{ width: status === 'active' ? '24px' : '8px' }}
              />
            );
          })}
        </div>
        <p className="font-caption text-muted-foreground text-sm">
          Step {currentStep + 1} of {steps?.length}:{' '}
          <span className="text-foreground font-medium">{steps?.[currentStep]?.label}</span>
        </p>
      </div>
    </nav>
  );
}